import React, { useState, useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { 
  Container, 
  Box, 
  CssBaseline,
  useMediaQuery,
  useTheme,
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Paper,
  Fade,
  Zoom,
  Button,
  Link,
  IconButton,
  Alert,
  Popover,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import HistoryIcon from '@mui/icons-material/History';
import PeopleIcon from '@mui/icons-material/People';
import DeleteIcon from '@mui/icons-material/Delete';
import AssessmentIcon from '@mui/icons-material/Assessment';
import GroupsIcon from '@mui/icons-material/Groups';
import CloudIcon from '@mui/icons-material/Cloud';
import CategoryIcon from '@mui/icons-material/Category';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ActaForm from './components/ActaForm';
import HistorialActas from './components/HistorialActas';
import UserAdmin from './components/UserAdmin';
import AdminActas from './components/AdminActas';
import AdminLogs from './components/AdminLogs';
import AdminComites from './components/AdminComites';
import AdminCategoriasOtro from './components/AdminCategoriasOtro';
import AdminApprovers from './components/AdminApprovers';
import AdminAiConfig from './components/AdminAiConfig';
import OneDriveManager from './components/OneDriveManager';
import SignaturePublic from './components/SignaturePublic';
import { db, auth, signInWithGoogle } from './firebase';
import { collection, addDoc, getDocs, query, orderBy, where, doc, updateDoc } from 'firebase/firestore';
import { assignActaToProjectResponsible, setupNewActaApproval } from './services/approvalService';
import { getProjectEmailWithFallback, saveCustomProjectEmail, getContractProjectEmailWithFallback, saveCustomContractProjectEmail } from './services/projectsApi';
import { logUserAction, LOG_ACTIONS } from './services/logService';
import styles from './styles/appStyles';
import { drawerWidth } from './constants';
import { getUserRole, ROLES, hasRole, generateContractProjectKey } from './userRoles';
import { VERSION_CONFIG, hasNewVersion, initializeVersion, setStoredVersion } from './config/versionConfig';
import { PDFViewer } from '@react-pdf/renderer';
import ActaPDF from './components/ActaPDF';

function App() {
  const location = useLocation();
  const [tab, setTab] = useState(0);
  const [isDrawerOpen, setIsDrawerOpen] = useState(true);
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [actas, setActas] = useState([]);
  const [currentActa, setCurrentActa] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nextActaNumber, setNextActaNumber] = useState(1);
  const [editingActa, setEditingActa] = useState(null);
  const [updateMessage, setUpdateMessage] = useState(null);
  const [emailPopupOpen, setEmailPopupOpen] = useState(false);
  const [customEmail, setCustomEmail] = useState('');
  const [pendingActaData, setPendingActaData] = useState(null);
  const [isSubmittingActa, setIsSubmittingActa] = useState(false);
  const [projects, setProjects] = useState([]);
  
  // Estados para el sistema de versioning
  const [showVersionDialog, setShowVersionDialog] = useState(false);
  const [isReloading, setIsReloading] = useState(false);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  // Cerrar el drawer automáticamente en móvil
  useEffect(() => {
    if (isMobile) {
      setIsDrawerOpen(false);
    }
  }, [isMobile]);

  // Check if we're on the signature route
  const isSignatureRoute = location.pathname.startsWith('/firma/');

  const [anchorEl, setAnchorEl] = useState(null);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  }

  // Cargar proyectos al inicializar la aplicación
  useEffect(() => {
    const loadProjects = async () => {
      try {
        const { fetchProjectsAndProducts } = await import('./services/projectsApi');
        const projectsData = await fetchProjectsAndProducts();
        
        // Procesar los datos para convertir el objeto Product en un array products
        const processedProjects = projectsData.map(project => {
          const products = [];
          if (project.Product) {
            Object.entries(project.Product).forEach(([key, value]) => {
              if (key.startsWith('product_') && value) {
                products.push(value);
              }
            });
          }
          
          return {
            ...project,
            products: products
          };
        });
        
        setProjects(processedProjects);
      } catch (error) {
        console.error('Error al cargar proyectos en App:', error);
      }
    };

    loadProjects();
  }, []);

  // Sistema de verificación de versión para forzar recargas
  useEffect(() => {
    // Inicializar versión en localStorage
    initializeVersion();
    
    // Verificar versión periódicamente
    const versionCheckInterval = setInterval(() => {
      if (hasNewVersion()) {
        console.log('🔄 Nueva versión detectada, mostrando diálogo de recarga');
        setShowVersionDialog(true);
      }
    }, VERSION_CONFIG.CHECK_INTERVAL);
    
    return () => {
      clearInterval(versionCheckInterval);
    };
  }, []);

  // Función para manejar la recarga forzada
  const handleForceReload = () => {
    setIsReloading(true);
    // Actualizar la versión almacenada antes de recargar
    setStoredVersion(VERSION_CONFIG.VERSION_NUMBER);
    // Recargar la página después de un breve delay
    setTimeout(() => {
      window.location.reload();
    }, 500);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'logout-popover' : undefined;

  // Cargar actas desde Firebase al iniciar
  useEffect(() => {
    // Verificar si hay un resultado de redirect pendiente al cargar la página
    const checkRedirectResult = async () => {
      try {
        const { getRedirectResult } = await import('firebase/auth');
        const result = await getRedirectResult(auth);
        
        if (result) {
          console.log('✅ Resultado de redirect obtenido:', result.user.email);
          // El usuario ya será manejado por onAuthStateChanged
        }
      } catch (error) {
        console.error('Error al verificar resultado de redirect:', error);
        if (error.code === 'auth/popup-closed-by-user') {
          setLoginError('El proceso de autenticación fue cancelado. Por favor, intenta nuevamente.');
        } else {
          setLoginError(error.message || 'Error en el proceso de autenticación');
        }
      }
    };

    // Listen for auth state changes
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      console.log('🔐 Auth state changed:', user ? `Usuario: ${user.email} (UID: ${user.uid})` : 'Usuario desconectado');
      setUser(user);
      
      if (user) {
        console.log('👤 Obteniendo rol para usuario:', user.email, 'con UID:', user.uid);
        // Get user role from Firestore
        const role = await getUserRole(user.uid);
        console.log('🎭 Rol obtenido:', role, 'para usuario:', user.email);
        setUserRole(role);
        
        // Limpiar errores de login si el usuario se autentica exitosamente
        setLoginError('');
      } else {
        setUserRole(null);
      }
      
      setIsLoading(false);
    });

    // Verificar resultado de redirect al cargar
    checkRedirectResult();

    return () => unsubscribe();
  }, []); // Removed dependencies to prevent infinite loop

  // Función para cargar actas desde Firebase
  const fetchActas = async () => {
    if (!user) return;
    
    try {
      const actasRef = collection(db, 'actas');
      let q;
      
      // Filter actas based on user role
      if (hasRole(userRole, ROLES.GESTOR) || hasRole(userRole, ROLES.APROBADOR)) {
        // Admin, Gestor and Aprobador can see all actas (filtered later by permissions)
        q = query(actasRef, orderBy('createdAt', 'desc'));
      } else {
        // Regular users can only see their own actas
        q = query(actasRef, orderBy('createdAt', 'desc'));
        // We'll filter by creatorId and allowedProjects later in the code
      }
      
      const querySnapshot = await getDocs(q);
      
      // Obtener proyectos asignados al usuario si no es gestor
      let userAllowedProjects = [];
      let userAllowedContractProjects = [];
      if (!hasRole(userRole, ROLES.GESTOR) && !hasRole(userRole, ROLES.APROBADOR)) {
        const { getUserAllowedProjects } = await import('./userRoles');
        userAllowedProjects = await getUserAllowedProjects(user.uid);
      } else if (hasRole(userRole, ROLES.APROBADOR)) {
        // Para aprobadores, obtener proyectos asignados y combinaciones específicas
        const { getUserAllowedProjects, getUserData } = await import('./userRoles');
        userAllowedProjects = await getUserAllowedProjects(user.uid);
        
        // Obtener combinaciones específicas de contrato/proyecto
        const userData = await getUserData(user.uid);
        userAllowedContractProjects = userData?.allowedContractProjects || [];
        
        console.log('🔍 FILTRADO EN APP.JSX PARA APROBADOR');
        console.log('Proyectos legacy asignados:', userAllowedProjects);
        console.log('Combinaciones asignadas:', userAllowedContractProjects);
      }
      
      const actasData = [];
      // Objeto para almacenar el número máximo de acta por proyecto y producto
      const maxActaNumbers = {
        global: 0,
        byProject: {},
        byProduct: {},
        byMultiProject: {},
        byMultiProjectProduct: {},
        byMultiProjectMultiProduct: {}
      };
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        
        // For regular users and aprobadores, filter by creatorId and allowedProjects
        if (!hasRole(userRole, ROLES.GESTOR)) {
          // El usuario puede ver el acta si:
          // 1. Es el creador del acta
          // 2. Tiene permisos para alguno de los proyectos del acta
          // 3. Para aprobadores: tiene asignación específica de contrato/proyecto
          const isCreator = data.creatorId === user.uid;
          
          let hasProjectAccess = false;
          let hasContractProjectAccess = false;
          
          // Verificar acceso por proyectos legacy
          hasProjectAccess = userAllowedProjects.some(allowedProject => {
            if (data.tipoProyectos && data.tipoProyectos.length > 0) {
              return data.tipoProyectos.includes(allowedProject);
            } else if (data.tipoProyecto) {
              return data.tipoProyecto === allowedProject;
            }
            return false;
          });
          
          // Para aprobadores, verificar combinaciones específicas de contrato/proyecto
          if (hasRole(userRole, ROLES.APROBADOR) && userAllowedContractProjects.length > 0) {
            // Primero verificar si el projectName del acta coincide directamente
             if (data.projectName) {
               const normalizedActaProjectName = data.projectName.trim();
               const hasMatch = userAllowedContractProjects.some(combo => 
                 combo.trim() === normalizedActaProjectName
               );
               if (hasMatch) {
                 hasContractProjectAccess = true;
               }
             }
             
             if (!hasContractProjectAccess) {
              // Si no hay projectName o no coincide, usar la lógica anterior
              const actaProjects = data.tipoProyectos && data.tipoProyectos.length > 0 
                ? data.tipoProyectos 
                : (data.tipoProyecto ? [data.tipoProyecto] : []);
              
              const actaProducts = data.productosProyecto && data.productosProyecto.length > 0 
                ? data.productosProyecto 
                : (data.productoProyecto ? [data.productoProyecto] : []);
              
              // Verificar cada combinación de proyecto/producto del acta
              for (const project of actaProjects) {
                if (actaProducts.length > 0) {
                  for (const product of actaProducts) {
                    const combinationKey = `${project.trim()}|${product.trim()}`;
                    if (userAllowedContractProjects.includes(combinationKey)) {
                      hasContractProjectAccess = true;
                      break;
                    }
                  }
                } else {
                  const combinationKey = `${project.trim()}|Sin producto específico`;
                  if (userAllowedContractProjects.includes(combinationKey)) {
                    hasContractProjectAccess = true;
                    break;
                  }
                }
                if (hasContractProjectAccess) break;
              }
            }
          }
          
          // Para aprobadores, también verificar si están asignados al acta por email
          const isAssignedApprover = hasRole(userRole, ROLES.APROBADOR) && 
            data.customProjectEmail && 
            user.email && 
            user.email.toLowerCase().trim() === data.customProjectEmail.toLowerCase().trim();
          
          if (!isCreator && !hasProjectAccess && !hasContractProjectAccess && !isAssignedApprover) {
            return;
          }
        }
        
        // Convertir las fechas de Firestore a objetos Date
        const acta = {
          ...data,
          id: doc.id,
          fecha: data.fecha ? data.fecha.toDate() : new Date(),
          horaInicio: data.horaInicio ? data.horaInicio.toDate() : new Date(),
          horaFinal: data.horaFinal ? data.horaFinal.toDate() : new Date(),
          compromisos: data.compromisos.map(c => ({
            ...c,
            fechaCumplimiento: c.fechaCumplimiento ? c.fechaCumplimiento.toDate() : new Date()
          })),
          // Manejar createdAt de forma segura
          createdAt: data.createdAt ? data.createdAt : null,
          // Asegurar que tipoProyectos y productosProyecto estén definidos como arrays
          tipoProyectos: data.tipoProyectos || (data.tipoProyecto ? [data.tipoProyecto] : []),
          productosProyecto: data.productosProyecto || (data.productoProyecto ? [data.productoProyecto] : [])
        };
        actasData.push(acta);
        
        // Track the highest acta number globally and by project/product
        // Solo considerar actas que NO son borradores para la numeración
        const actaNum = parseInt(acta.numeroActa, 10);
        if (!isNaN(actaNum) && acta.approvalStatus !== 'borrador' && !acta.isDraft) {
          // Global max
          if (actaNum > maxActaNumbers.global) {
            maxActaNumbers.global = actaNum;
          }
          
          // Obtener proyectos y productos del acta
          const proyectos = acta.tipoProyectos && acta.tipoProyectos.length > 0 
            ? acta.tipoProyectos 
            : (acta.tipoProyecto ? [acta.tipoProyecto] : ['sin_proyecto']);
          
          const productos = acta.productosProyecto && acta.productosProyecto.length > 0 
            ? acta.productosProyecto 
            : (acta.productoProyecto ? [acta.productoProyecto] : []);
          
          // Generar claves para numeración
          const projectKey = proyectos.sort().join('_');
          const productKey = productos.length > 0 ? `${projectKey}_${productos.sort().join('_')}` : null;
          
          // By project (compatibilidad)
          if (proyectos.length === 1) {
            const singleProject = proyectos[0];
            if (!maxActaNumbers.byProject[singleProject] || actaNum > maxActaNumbers.byProject[singleProject]) {
              maxActaNumbers.byProject[singleProject] = actaNum;
            }
          }
          
          // By multi-project
          if (!maxActaNumbers.byMultiProject) maxActaNumbers.byMultiProject = {};
          if (!maxActaNumbers.byMultiProject[projectKey] || actaNum > maxActaNumbers.byMultiProject[projectKey]) {
            maxActaNumbers.byMultiProject[projectKey] = actaNum;
          }
          
          // By product (compatibilidad)
          if (productos.length === 1 && proyectos.length === 1) {
            const singleProduct = productos[0];
            const singleProject = proyectos[0];
            const legacyProductKey = `${singleProject}_${singleProduct}`;
            
            if (!maxActaNumbers.byProduct[legacyProductKey] || actaNum > maxActaNumbers.byProduct[legacyProductKey]) {
              maxActaNumbers.byProduct[legacyProductKey] = actaNum;
            }
          }
          
          // By multi-project and products
          if (productos.length > 0) {
            if (!maxActaNumbers.byMultiProjectProduct) maxActaNumbers.byMultiProjectProduct = {};
            if (!maxActaNumbers.byMultiProjectProduct[productKey] || actaNum > maxActaNumbers.byMultiProjectProduct[productKey]) {
              maxActaNumbers.byMultiProjectProduct[productKey] = actaNum;
            }
          }
        }
      });
      
      setActas(actasData);
      // Almacenar la información de numeración en el estado para usarla más tarde
      setNextActaNumber(maxActaNumbers.global + 1);
      // Guardar la información de numeración por proyecto/producto en una variable global
      window.actaNumbering = maxActaNumbers;
    } catch (error) {
      console.error('Error al cargar actas:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Cargar actas al iniciar o cuando cambia el usuario o rol
  useEffect(() => {
    fetchActas();
  }, [user, userRole]);


  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  const handleLogin = async () => {
    setLoginLoading(true);
    setLoginError('');
    
    try {
      const result = await signInWithGoogle();
      
      // Si el resultado es null, significa que se está usando redirect
      if (result === null) {
        // El usuario será redirigido, no necesitamos hacer nada más
        return;
      }
      
      console.log('Usuario autenticado exitosamente:', result.email);
      
    } catch (error) {
      console.error('Error al iniciar sesión:', error);
      setLoginError(error.message || 'Error desconocido al iniciar sesión');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    auth.signOut();
  };

  // If we're on the signature route, render only the SignaturePublic component (before auth checks)
  if (isSignatureRoute) {
    return (
      <>
        <CssBaseline />
        <Routes>
          <Route path="/firma/:actaId" element={<SignaturePublic />} />
        </Routes>
      </>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography>Cargando...</Typography>
      </Box>
    );
  }

  if (!user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', gap: 3, bgcolor: '#fff', p: 4 }}>
        <Box sx={{ width: '100%', maxWidth: 400, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
          <Box component="img" src="https://herramientas.iets.org.co/flujogramaiets/images/logo-IETS.png" alt="IETS Logo" sx={{ width: 200, mb: 2 }} />
          <Typography variant="h5" sx={{ color: '#1e293b', textAlign: 'center', mb: 1 }}>Bienvenid@ al generador de actas!</Typography>
          <Typography variant="body1" sx={{ color: '#64748b', textAlign: 'center', mb: 3 }}>
            Debes iniciar sesión con tu cuenta de correo IETS,
            de lo contrario no podrás ingresar.
          </Typography>
          {loginError && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {loginError}
            </Alert>
          )}
          <Button
            variant="outlined"
            onClick={handleLogin}
            disabled={loginLoading}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              py: 1.5,
              px: 3,
              borderColor: '#e2e8f0',
              color: '#64748b',
              '&:hover': {
                borderColor: '#cbd5e1',
                bgcolor: '#f8fafc'
              },
              '&:disabled': {
                borderColor: '#e2e8f0',
                color: '#94a3b8'
              }
            }}
          >
            {loginLoading ? (
              <CircularProgress size={20} sx={{ color: '#64748b' }} />
            ) : (
              <Box component="img" src="https://www.google.com/images/branding/googleg/1x/googleg_standard_color_128dp.png" alt="Google" sx={{ width: 20, height: 20 }} />
            )}
            {loginLoading ? 'Iniciando sesión...' : 'Acceder con Google'}
          </Button>
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: '#64748b' }}>¿No puedes ingresar?</Typography>
            <Link
              href={`mailto:soporte@iets.org.co`}
              sx={{
                color: '#2563eb',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              Ponte en contacto con el administrador
            </Link>
          </Box>
        </Box>
      </Box>
    );
  }

  const handleSubmit = async (formData, setIsSubmitting) => {
    try {
      setIsSubmittingActa(true);
      
      // Debug: Verificar el estado de editingActa
      console.log('🔍 handleSubmit - editingActa:', editingActa);
      console.log('🔍 handleSubmit - editingActa ID:', editingActa?.id);

      // Verificar si es un acta de Comité o Otro sin vinculación a proyectos
      const isComiteType = formData.tipoActa === 'COMITE' || (typeof formData.tipoActa === 'string' && formData.tipoActa.startsWith('COMITE'));
      const isOtroType = formData.tipoActa === 'OTRO' || (typeof formData.tipoActa === 'string' && formData.tipoActa !== 'REUNION' && !formData.tipoActa.startsWith('COMITE'));
      
      const isDirecto = (isComiteType || isOtroType) && 
                       (!formData.tipoProyectos || formData.tipoProyectos.length === 0 || 
                        (formData.tipoProyectos.length === 1 && formData.tipoProyectos[0] === 'sin_proyecto'));

      // Modificar proyectos si es directa
      if (isDirecto) {
        // En Actas directas, el formData ya viene con el tipoProyecto correcto desde prepareSubmissionData en ActaForm
        // Usaremos ese valor si existe, o caeremos en la lógica anterior
        const actaName = formData.tipoProyecto || (formData.tipoActa === 'OTRO' ? (formData.tipoActaCustom || 'OTRO') : formData.tipoActa);
        console.log(`👤 Acta directa (Comité/Otro sin proyectos): Usando ${actaName} como proyecto`);
        formData.tipoProyectos = [actaName];
        formData.tipoProyecto = actaName;
      }
      
      // Verificar si estamos editando un acta existente
      if (editingActa && editingActa.id) {
        console.log('✏️ Actualizando acta existente:', editingActa.id);
        
        if (isDirecto) {
          const actaName = formData.tipoProyecto;
          const projectEmail = await getProjectEmailWithFallback(actaName);
          
          if (!projectEmail) {
            console.log(`👤 No se encontró correo para ${actaName} al actualizar, solicitando...`);
            setPendingActaData({
              ...formData,
              proyectos: [actaName],
              productos: [],
              isDirecto: true,
              isUpdate: true
            });
            setEmailPopupOpen(true);
            return;
          }
        }
        
        await updateActa(formData);
        return;
      }
      
      console.log('➕ Creando nueva acta');

      let projectEmail = null;
      
      // Verificar si hay proyectos seleccionados (usar tipoProyectos del formulario)
      const proyectosIds = formData.tipoProyectos && formData.tipoProyectos.length > 0 
        ? formData.tipoProyectos 
        : (formData.tipoProyecto ? [formData.tipoProyecto] : []);
      
      // Convertir IDs de proyectos a nombres
      let proyectosNombres = proyectosIds
        .map(projectId => {
          if (projectId === 'OTRO') {
            return formData.tipoProyectoCustom || 'Proyecto personalizado';
          }
          const proyecto = projects.find(p => p.id === projectId);
          return proyecto ? proyecto.name : projectId; // Fallback al ID si no se encuentra
        })
        .filter(name => name);
      
      const productos = formData.productosProyecto || [];

      // Si es un acta directa, pedir el correo si no existe
      if (isDirecto) {
        const actaName = formData.tipoProyecto;
        proyectosNombres = [actaName];
        
        projectEmail = await getProjectEmailWithFallback(actaName);
        
        if (!projectEmail) {
          console.log(`👤 No se encontró correo para ${actaName}, solicitando...`);
          setPendingActaData({
            ...formData,
            proyectos: [actaName],
            productos: [],
            isDirecto: true
          });
          setEmailPopupOpen(true);
          return;
        }
      }
      // Si hay múltiples proyectos o proyectos con productos, usar la lógica de combinación
      else if (proyectosNombres.length > 1 || (proyectosNombres.length === 1 && productos.length > 0)) {
        console.log('🔍 Buscando email para combinación contrato+proyecto:', { proyectosNombres, productos });
        // Intentar obtener email para la combinación contrato/proyecto
        projectEmail = await getContractProjectEmailWithFallback(proyectosNombres, productos);
        
        if (!projectEmail) {
          // Si no hay email para la combinación, mostrar popup para solicitarlo
          setPendingActaData({
            ...formData,
            proyectos: proyectosNombres,
            productos: productos
          });
          setEmailPopupOpen(true);
          return;
        }
      }
      // Si hay un solo proyecto sin productos, usar la lógica individual
      else if (proyectosNombres.length === 1) {
        const projectName = proyectosNombres[0];
        
        if (projectName && projectName !== 'Sin proyecto') {
          projectEmail = await getProjectEmailWithFallback(projectName);
          
          if (!projectEmail) {
            // Si no se encuentra el correo, mostrar popup para solicitarlo
            setPendingActaData({
              ...formData,
              proyectos: [projectName],
              productos: []
            });
            setEmailPopupOpen(true);
            return;
          }
        }
      }

      // Si se encuentra el correo o no hay proyecto/combinación, continuar con el proceso normal
      await processActaSubmission(formData, projectEmail, proyectosNombres);
    } catch (error) {
      console.error('Error al guardar el acta:', error);
      alert('Error al guardar el acta. Por favor intente nuevamente.');
    } finally {
      setIsSubmittingActa(false);
      if (setIsSubmitting) setIsSubmitting(false);
    }
  };

  // Función separada para procesar el envío del acta
  const processActaSubmission = async (formData, customProjectEmail = null, resolvedProjectNames = null) => {
    try {
      // Si es un borrador, no asignar número de acta definitivo
      const isDraft = formData.approvalStatus === 'borrador' || formData.isDraft;
      
      // Determinar el contexto (proyectos y/o productos) - definir fuera del bloque condicional
      // Usar tipoProyectos si está disponible, o caer en tipoProyecto para compatibilidad
      const proyectos = formData.tipoProyectos && formData.tipoProyectos.length > 0 
        ? formData.tipoProyectos 
        : (formData.tipoProyecto ? [formData.tipoProyecto] : ['sin_proyecto']);
      
      // Resolver nombres de proyectos si no se pasaron explícitamente
      let projectNames = resolvedProjectNames;
      if (!projectNames) {
        // Intentar resolver nombres usando la lista de proyectos disponible en el estado
        projectNames = proyectos.map(projectId => {
          if (projectId === 'OTRO') return formData.tipoProyectoCustom || 'Proyecto personalizado';
          if (projectId === 'sin_proyecto') return 'Sin proyecto';
          const p = projects.find(proj => proj.id === projectId);
          return p ? p.name : projectId;
        });
      }

      // Generar un ID único para el conjunto de proyectos
      const projectKey = proyectos.sort().join('_');
      
      // Usar productosProyecto si está disponible, o caer en productoProyecto para compatibilidad
      const productos = formData.productosProyecto && formData.productosProyecto.length > 0 
        ? formData.productosProyecto 
        : (formData.productoProyecto ? [formData.productoProyecto] : []);
      
      const hasProduct = productos.length > 0;
      const productKey = hasProduct ? `${projectKey}_${productos.sort().join('_')}` : null;
      
      if (!isDraft) {
        // Solo verificar y asignar números para actas que no son borradores
        const actasRef = collection(db, 'actas');

        // Verificar si es un acta de Comité sin proyectos (vinculación directa a comité)
         const isComiteType = formData.tipoActa === 'COMITE' || (typeof formData.tipoActa === 'string' && formData.tipoActa.startsWith('COMITE'));
         const isComiteDirecto = isComiteType && 
                                (!formData.tipoProyectos || formData.tipoProyectos.length === 0 || 
                                 (formData.tipoProyectos.length === 1 && (formData.tipoProyectos[0] === 'sin_proyecto' || formData.tipoProyectos[0] === formData.tipoProyecto)));
         
        // Verificar si es un acta de OTRO sin proyectos (vinculación directa a categoría)
         const isOtroType = formData.tipoActa === 'OTRO' || (typeof formData.tipoActa === 'string' && formData.tipoActa !== 'REUNION' && !formData.tipoActa.startsWith('COMITE'));
         const isOtroDirecto = isOtroType && 
                                (!formData.tipoProyectos || formData.tipoProyectos.length === 0 || 
                                 (formData.tipoProyectos.length === 1 && (formData.tipoProyectos[0] === 'sin_proyecto' || formData.tipoProyectos[0] === formData.tipoProyecto)));

         if (isOtroDirecto && formData.tipoActaCustom) {
              // Lógica específica para OTRO - Consultar por tipoActaCustom
              const q = query(actasRef, where('tipoActaCustom', '==', formData.tipoActaCustom), where('tipoActa', '==', 'OTRO'));
              const querySnapshot = await getDocs(q);
              let maxNum = 0;
              let currentNumExists = false;
              
              const sentNum = parseInt(formData.numeroActa, 10);
 
              querySnapshot.forEach(doc => {
                 const data = doc.data();
                 // Verificar que no tenga proyectos vinculados reales
                 const proyectos = data.tipoProyectos || [];
                 const hasProjects = proyectos.length > 0 && 
                                    !proyectos.every(p => 
                                      p === 'sin_proyecto' || 
                                      p === 'Proyecto sin especificar' || 
                                      p === data.tipoProyecto || 
                                      p === data.tipoActa ||
                                      p === data.tipoActaCustom
                                    );
                 
                 if (data.approvalStatus !== 'borrador' && !data.isDraft && !hasProjects) {
                     const num = parseInt(data.numeroActa, 10);
                     if (!isNaN(num)) {
                         if (num > maxNum) maxNum = num;
                         if (num === sentNum) currentNumExists = true;
                     }
                 }
              });
              
              if (currentNumExists) {
                  formData.numeroActa = (maxNum + 1).toString();
              }
         } else if (isComiteDirecto && formData.comiteId) {
              // Lógica específica para comité - Consultar por ID de comité
              // Nota: No filtramos por tipoActa exacto porque puede venir como "COMITE - Nombre"
              const q = query(actasRef, where('comiteId', '==', formData.comiteId));
              const querySnapshot = await getDocs(q);
              let maxNum = 0;
              let currentNumExists = false;
              
              const sentNum = parseInt(formData.numeroActa, 10);
 
              querySnapshot.forEach(doc => {
                 const data = doc.data();
                 // Verificar que sea un acta de comité (por si acaso)
                 const isComiteData = data.tipoActa === 'COMITE' || (data.tipoActa && data.tipoActa.startsWith('COMITE'));
                 
                 if (isComiteData) {
                     // Verificar que no tenga proyectos vinculados reales
                     const proyectos = data.tipoProyectos || [];
                     const hasProjects = proyectos.length > 0 && 
                                        !proyectos.every(p => 
                                          p === 'sin_proyecto' || 
                                          p === 'Proyecto sin especificar' || 
                                          p === data.tipoProyecto || 
                                          p === data.tipoActa
                                        );
                     if (data.approvalStatus !== 'borrador' && !data.isDraft && !hasProjects) {
                         const num = parseInt(data.numeroActa, 10);
                         if (!isNaN(num)) {
                             if (num > maxNum) maxNum = num;
                             if (num === sentNum) currentNumExists = true;
                         }
                     }
                 }
              });
             
             if (currentNumExists) {
                 formData.numeroActa = (maxNum + 1).toString();
             }
        } else {
            const q = query(actasRef, orderBy('createdAt', 'desc'));
            const querySnapshot = await getDocs(q);
            
            let maxActaNumber = 0;
            let actaNumberExists = false;
            
            querySnapshot.forEach((doc) => {
              const data = doc.data();
              // Solo considerar actas que NO son borradores para la numeración
              if (data.approvalStatus !== 'borrador' && !data.isDraft) {
                const actaNum = parseInt(data.numeroActa, 10);
                
                if (!isNaN(actaNum)) {
                  // Verificar si el número ya existe en el mismo contexto (proyectos/productos)
                  // Obtener los proyectos y productos del acta existente
                  const actaProyectos = data.tipoProyectos && data.tipoProyectos.length > 0 
                    ? data.tipoProyectos 
                    : (data.tipoProyecto ? [data.tipoProyecto] : ['sin_proyecto']);
                  
                  const actaProductos = data.productosProyecto && data.productosProyecto.length > 0 
                    ? data.productosProyecto 
                    : (data.productoProyecto ? [data.productoProyecto] : []);
                  
                  // Generar claves para comparación
                  const actaProjectKey = actaProyectos.sort().join('_');
                  const actaProductKey = actaProductos.length > 0 ? `${actaProjectKey}_${actaProductos.sort().join('_')}` : null;
                  
                  // Verificar duplicados según el contexto
                  if (actaNum === parseInt(formData.numeroActa, 10)) {
                    if (hasProduct && productKey === actaProductKey) {
                      // Duplicado en la misma combinación de proyectos y productos
                      actaNumberExists = true;
                    } else if (!hasProduct && projectKey === actaProjectKey && !actaProductos.length) {
                      // Duplicado en la misma combinación de proyectos (sin productos)
                      actaNumberExists = true;
                    }
                  }
                  
                  // Actualizar el número máximo según el contexto
                  if (hasProduct && productKey === actaProductKey && actaNum > maxActaNumber) {
                    maxActaNumber = actaNum;
                  } else if (!hasProduct && projectKey === actaProjectKey && !actaProductos.length && actaNum > maxActaNumber) {
                    maxActaNumber = actaNum;
                  }
                }
              }
            });

            if (actaNumberExists) {
              // Si el número ya existe en este contexto, asignar el siguiente número disponible
              formData.numeroActa = (maxActaNumber + 1).toString();
            }
        }
      } else {
        // Para borradores, usar un identificador temporal
        formData.numeroActa = `BORRADOR-${Date.now()}`;
      }
      
      // Actualizar la información de numeración global
      if (!window.actaNumbering) {
        window.actaNumbering = {
          global: 0,
          byProject: {},
          byProduct: {},
          byMultiProject: {},
          byMultiProjectProduct: {},
          byMultiProjectMultiProduct: {}
        };
      }
      
      const actaNum = parseInt(formData.numeroActa, 10);
      if (!isNaN(actaNum)) {
        // Actualizar máximos globales
        window.actaNumbering.global = Math.max(window.actaNumbering.global, actaNum);
        
        // Actualizar máximos por proyecto individual (para compatibilidad)
        if (proyectos.length === 1) {
          const singleProject = proyectos[0];
          window.actaNumbering.byProject[singleProject] = Math.max(
            window.actaNumbering.byProject[singleProject] || 0, 
            actaNum
          );
        }
        
        // Actualizar máximos por conjunto de proyectos
        window.actaNumbering.byMultiProject = window.actaNumbering.byMultiProject || {};
        window.actaNumbering.byMultiProject[projectKey] = Math.max(
          window.actaNumbering.byMultiProject[projectKey] || 0, 
          actaNum
        );
        
        // Actualizar máximos por producto si aplica (para compatibilidad)
        if (productos.length === 1 && proyectos.length === 1) {
          const singleProduct = productos[0];
          const singleProject = proyectos[0];
          const legacyProductKey = `${singleProject}_${singleProduct}`;
          
          window.actaNumbering.byProduct[legacyProductKey] = Math.max(
            window.actaNumbering.byProduct[legacyProductKey] || 0,
            actaNum
          );
        }
        
        // Actualizar máximos por conjunto de proyectos y productos
        if (hasProduct && productKey) {
          window.actaNumbering.byMultiProjectProduct = window.actaNumbering.byMultiProjectProduct || {};
          window.actaNumbering.byMultiProjectProduct[productKey] = Math.max(
            window.actaNumbering.byMultiProjectProduct[productKey] || 0,
            actaNum
          );
        }
      }

      // Función para limpiar y validar datos para Firebase (evita arrays anidados y tipos inválidos)
      const cleanDataForFirebase = (data) => {
        const cleanedData = { ...data };
        
        // Si objetivo es un objeto enriquecido con bloques y tablas, convertirlo a string JSON
        // para evitar el error de Firebase: "Nested arrays are not supported"
        if (cleanedData.objetivo && typeof cleanedData.objetivo === 'object') {
          cleanedData.objetivo = JSON.stringify(cleanedData.objetivo);
        }

        // Limpiar y validar imagenesAdjuntas
        if (cleanedData.imagenesAdjuntas && Array.isArray(cleanedData.imagenesAdjuntas)) {
          cleanedData.imagenesAdjuntas = cleanedData.imagenesAdjuntas.map(img => {
            // Asegurar que cada imagen sea un objeto simple con propiedades string
            return {
              id: String(img.id || ''),
              nombre: String(img.nombre || ''),
              imagen: String(img.imagen || '')
            };
          }).filter(img => img.id && img.imagen); // Filtrar imágenes inválidas
        }
        
        return cleanedData;
      };
      
      // Crear un nuevo objeto con la fecha de creación y el ID del creador
      // Asegurarse de que los campos tipoProyectos y productosProyecto estén incluidos
      const actaData = {
        ...cleanDataForFirebase(formData),
        // Asegurar que tipoProyectos esté definido como array
        tipoProyectos: proyectos,
        // Asegurar que productosProyecto esté definido como array
        productosProyecto: productos,
        // Mantener tipoProyecto y productoProyecto para compatibilidad
        // Para combinaciones contrato/proyecto, verificar si ya están en formato correcto
        // USAR NOMBRES DE PROYECTOS, NO IDs, para la generación de claves
        tipoProyecto: projectNames.length === 1 && productos.length > 0
          ? (projectNames[0].includes('|') ? projectNames[0] : generateContractProjectKey(projectNames[0], productos[0]))
          : (projectNames.length === 1 ? projectNames[0] : (projectNames.length > 1 ? projectNames.join(', ') : formData.tipoProyecto)),
        productoProyecto: productos.length > 0 ? productos.join(', ') : 'Sin producto',
        // Agregar campo projectName que coincida con el formato de allowedContractProjects
        // USAR NOMBRES DE PROYECTOS, NO IDs
        projectName: projectNames.length === 1 && productos.length > 0
          ? (projectNames[0].includes('|') ? projectNames[0] : generateContractProjectKey(projectNames[0], productos[0]))
          : (projectNames.length === 1 ? projectNames[0] : (projectNames.length > 1 ? projectNames.join(', ') : formData.tipoProyecto)),
        createdAt: new Date(),
        createdBy: user.uid,
        creatorId: user.uid,
        creatorEmail: user.email,
        // Incluir el correo personalizado del encargado del proyecto si se proporcionó
        ...(customProjectEmail && { customProjectEmail })
      };
      
      // Aplicar configuración de aprobación
      const newActa = setupNewActaApproval(actaData);
      
      // Guardar en Firebase
      const docRef = await addDoc(collection(db, 'actas'), newActa);
      
      // Asignar el acta al responsable del proyecto automáticamente
      const actaWithId = { ...newActa, id: docRef.id };
      await assignActaToProjectResponsible(actaWithId, projectNames);
      
      // Registrar la acción en el sistema de logs
      const actaDetails = {
        numeroActa: formData.numeroActa,
        tipoActa: formData.tipoActa === 'OTRO' ? formData.tipoActaCustom : formData.tipoActa,
        tipoProyecto: formData.tipoProyecto === 'OTRO' ? formData.tipoProyectoCustom : formData.tipoProyecto,
        productoProyecto: formData.productoProyecto || ''
      };
      await logUserAction(LOG_ACTIONS.CREATE, 'acta', docRef.id, actaDetails);
      
      // Actualizar el estado local con el ID de Firestore
      // Asegurarse de que las fechas sean objetos Date para el PDF
      const actaWithFormattedDates = {
        ...newActa,
        id: docRef.id,
        // Asegurar que las fechas sean objetos Date
        fecha: newActa.fecha instanceof Date ? newActa.fecha : new Date(newActa.fecha),
        horaInicio: newActa.horaInicio instanceof Date ? newActa.horaInicio : new Date(newActa.horaInicio),
        horaFinal: newActa.horaFinal instanceof Date ? newActa.horaFinal : new Date(newActa.horaFinal),
        compromisos: newActa.compromisos.map(c => ({
          ...c,
          fechaCumplimiento: c.fechaCumplimiento instanceof Date ? c.fechaCumplimiento : new Date(c.fechaCumplimiento)
        }))
      };
      
      // Asignar el acta al usuario responsable del proyecto
      await assignActaToProjectResponsible(actaWithFormattedDates, projectNames);
      
      // Actualizar el estado local primero
      setCurrentActa(actaWithFormattedDates);
      setActas([actaWithFormattedDates, ...actas]);
      
      // Solo incrementar el número de acta si no es un borrador
      if (!isDraft) {
        setNextActaNumber(parseInt(formData.numeroActa, 10) + 1);
      }
      
      // Cambiar a la pestaña de vista previa después de que el estado se haya actualizado
      setTimeout(() => {
        setTab(2); // Cambiar a la pestaña de vista previa
      }, 100);
    } catch (error) {
      console.error('Error al guardar el acta:', error);
      alert('Error al guardar el acta. Por favor intente nuevamente.');
    }
  };

  // Función para manejar el envío del correo personalizado
  const handleCustomEmailSubmit = async () => {
    if (!customEmail.trim()) {
      alert('Por favor ingrese un correo electrónico válido.');
      return;
    }

    // Validar formato de correo básico
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(customEmail)) {
      alert('Por favor ingrese un correo electrónico con formato válido.');
      return;
    }

    // Activar estado de carga
    setIsSubmittingActa(true);

    try {
      // Verificar si hay proyectos y productos seleccionados (combinación contrato/proyecto)
      if (pendingActaData.proyectos && pendingActaData.proyectos.length > 0) {
        const productos = pendingActaData.productos || [];
        
        // Guardar el email personalizado para la combinación contrato/proyecto
        await saveCustomContractProjectEmail(pendingActaData.proyectos, productos, customEmail);
      }
      // Verificar si hay un proyecto individual seleccionado (compatibilidad hacia atrás)
      else {
        // Determinar el nombre del proyecto para guardar el email
        const projectName = pendingActaData.tipoProyecto === 'OTRO' ? 
                           pendingActaData.tipoProyectoCustom : 
                           pendingActaData.tipoProyecto;
        
        if (projectName && projectName !== 'Sin proyecto') {
          // Guardar el email personalizado para futuros usos
          await saveCustomProjectEmail(projectName, customEmail);
        }
      }
      
      // Agregar el correo personalizado al acta
      const actaDataWithCustomEmail = {
        ...pendingActaData,
        customProjectEmail: customEmail
      };

      if (pendingActaData.isUpdate) {
        // Actualizar el acta con el correo personalizado
        await updateActa(actaDataWithCustomEmail);
      } else {
        // Procesar el acta con el correo personalizado
        await processActaSubmission(actaDataWithCustomEmail, customEmail, pendingActaData.proyectos);
      }
      
      // Cerrar el popup y limpiar estados
      setEmailPopupOpen(false);
      setCustomEmail('');
      setPendingActaData(null);
      setIsSubmittingActa(false);
    } catch (error) {
      console.error('Error al procesar el acta con correo personalizado:', error);
      alert('Error al procesar el acta. Por favor intente nuevamente.');
      setIsSubmittingActa(false);
    }
  };

  // Función para cancelar el popup del correo
  const handleEmailPopupCancel = () => {
    setEmailPopupOpen(false);
    setCustomEmail('');
    setPendingActaData(null);
    setIsSubmittingActa(false);
  };

  // Función para actualizar un acta existente
  const updateActa = async (formData) => {
    try {
      console.log('🔄 Iniciando actualización de acta:', editingActa.id);
      console.log('📝 Datos del formulario:', formData);
      
      // Referencia al documento del acta
      const actaRef = doc(db, 'actas', editingActa.id);
      
      // Verificar si se está convirtiendo de borrador a acta real
      const wasADraft = editingActa.approvalStatus === 'borrador' || editingActa.isDraft;
      const isNowReal = formData.approvalStatus !== 'borrador' && !formData.isDraft;
      const isConvertingFromDraft = wasADraft && isNowReal;
      
      // Determinar proyectos y productos
      const proyectos = formData.tipoProyectos && formData.tipoProyectos.length > 0 
        ? formData.tipoProyectos 
        : (formData.tipoProyecto ? [formData.tipoProyecto] : ['sin_proyecto']);
      
      const productos = formData.productosProyecto && formData.productosProyecto.length > 0 
        ? formData.productosProyecto 
        : (formData.productoProyecto ? [formData.productoProyecto] : []);
      
      // Resolver nombres de proyectos (igual que en processActaSubmission)
      const projectNames = proyectos.map(projectId => {
          if (projectId === 'OTRO') return formData.tipoProyectoCustom || 'Proyecto personalizado';
          if (projectId === 'sin_proyecto') return 'Sin proyecto';
          const p = projects.find(proj => proj.id === projectId);
          return p ? p.name : projectId;
      });
      
      // Si se está convirtiendo de borrador, asignar un número de acta real
      let finalNumeroActa = formData.numeroActa;
      if (isConvertingFromDraft) {
        // Obtener el siguiente número disponible para este contexto
        const actasRef = collection(db, 'actas');
        const q = query(actasRef, orderBy('createdAt', 'desc'));
        const querySnapshot = await getDocs(q);
        
        let maxActaNumber = 0;
        const projectKey = proyectos.sort().join('_');
        const hasProduct = productos.length > 0;
        const productKey = hasProduct ? `${projectKey}_${productos.sort().join('_')}` : null;
        
        querySnapshot.forEach((docSnap) => {
          const data = docSnap.data();
          // Solo considerar actas que NO son borradores
          if (data.approvalStatus !== 'borrador' && !data.isDraft) {
            const actaNum = parseInt(data.numeroActa, 10);
            
            if (!isNaN(actaNum)) {
              const actaProyectos = data.tipoProyectos && data.tipoProyectos.length > 0 
                ? data.tipoProyectos 
                : (data.tipoProyecto ? [data.tipoProyecto] : ['sin_proyecto']);
              
              const actaProductos = data.productosProyecto && data.productosProyecto.length > 0 
                ? data.productosProyecto 
                : (data.productoProyecto ? [data.productoProyecto] : []);
              
              const actaProjectKey = actaProyectos.sort().join('_');
              const actaProductKey = actaProductos.length > 0 ? `${actaProjectKey}_${actaProductos.sort().join('_')}` : null;
              
              // Verificar si es del mismo contexto
              if (hasProduct && productKey === actaProductKey && actaNum > maxActaNumber) {
                maxActaNumber = actaNum;
              } else if (!hasProduct && projectKey === actaProjectKey && !actaProductos.length && actaNum > maxActaNumber) {
                maxActaNumber = actaNum;
              }
            }
          }
        });
        
        finalNumeroActa = (maxActaNumber + 1).toString();
        
        // Actualizar el nextActaNumber global
        setNextActaNumber(maxActaNumber + 2);
      }
      
      // Asegurar que objetivo se serialice a string si es objeto para evitar error de arrays anidados en Firestore
      const objetivoCleaned = formData.objetivo && typeof formData.objetivo === 'object'
        ? JSON.stringify(formData.objetivo)
        : formData.objetivo;

      // Crear objeto con los datos actualizados
      const updatedActa = {
        ...formData,
        objetivo: objetivoCleaned,
        numeroActa: finalNumeroActa,
        // Asegurar que tipoProyectos esté definido como array
        tipoProyectos: proyectos,
        // Asegurar que productosProyecto esté definido como array
        productosProyecto: productos,
        // Mantener tipoProyecto y productoProyecto para compatibilidad
        // Para combinaciones contrato/proyecto, verificar si ya están en formato correcto
        tipoProyecto: projectNames.length === 1 && productos.length > 0
          ? (projectNames[0].includes('|') ? projectNames[0] : generateContractProjectKey(projectNames[0], productos[0]))
          : (projectNames.length === 1 ? projectNames[0] : (projectNames.length > 1 ? projectNames.join(', ') : formData.tipoProyecto)),
        productoProyecto: productos.length === 1 ? productos[0] : (productos.length > 0 ? productos.join(', ') : ''),
        // Agregar campo projectName que coincida con el formato de allowedContractProjects
        projectName: projectNames.length === 1 && productos.length > 0
          ? (projectNames[0].includes('|') ? projectNames[0] : generateContractProjectKey(projectNames[0], productos[0]))
          : (projectNames.length === 1 ? projectNames[0] : (projectNames.length > 1 ? projectNames.join(', ') : formData.tipoProyecto)),
        // Mantener los datos originales que no deben cambiar
        createdAt: editingActa.createdAt,
        createdBy: editingActa.createdBy || editingActa.creatorId,
        creatorId: editingActa.creatorId,
        creatorEmail: editingActa.creatorEmail,
        // Asegurar que el estado de aprobación esté definido
        approvalStatus: formData.approvalStatus || 'por aprobar',
        // Añadir información de actualización
        updatedAt: new Date(),
        updatedBy: user.uid
      };
      
      // Actualizar en Firestore
      await updateDoc(actaRef, updatedActa);
      
      // Registrar la acción en el sistema de logs
      const actaDetails = {
        numeroActa: formData.numeroActa,
        tipoActa: formData.tipoActa === 'OTRO' ? formData.tipoActaCustom : formData.tipoActa,
        tipoProyecto: formData.tipoProyecto === 'OTRO' ? formData.tipoProyectoCustom : formData.tipoProyecto,
        productoProyecto: formData.productoProyecto || ''
      };
      await logUserAction(LOG_ACTIONS.UPDATE, 'acta', editingActa.id, actaDetails);
      
      // Actualizar el estado local
      const updatedActaWithId = {
        ...updatedActa,
        id: editingActa.id,
        // Asegurar que las fechas sean objetos Date
        fecha: updatedActa.fecha instanceof Date ? updatedActa.fecha : new Date(updatedActa.fecha),
        horaInicio: updatedActa.horaInicio instanceof Date ? updatedActa.horaInicio : new Date(updatedActa.horaInicio),
        horaFinal: updatedActa.horaFinal instanceof Date ? updatedActa.horaFinal : new Date(updatedActa.horaFinal),
        compromisos: updatedActa.compromisos.map(c => ({
          ...c,
          fechaCumplimiento: c.fechaCumplimiento instanceof Date ? c.fechaCumplimiento : new Date(c.fechaCumplimiento)
        }))
      };
      
      // Re-asignar el acta al usuario responsable del proyecto con los nuevos datos
      // Esto actualizará los encargados de aprobación basados en los nuevos datos
      await assignActaToProjectResponsible(updatedActaWithId, projectNames);
      
      // Si se cambiaron los proyectos, customProjectEmail podría ya no ser válido
      // Obtener el nuevo email del aprobador para actualizar el documento si cambió
      const newApproverEmail = await getContractProjectEmailWithFallback(projectNames, productos);
      
      if (newApproverEmail && newApproverEmail !== updatedActa.customProjectEmail) {
        console.log(`🔄 Actualizando customProjectEmail de ${updatedActa.customProjectEmail} a ${newApproverEmail}`);
        
        // Actualizar el documento en Firestore
        await updateDoc(actaRef, {
          customProjectEmail: newApproverEmail
        });
        
        // Actualizar el objeto local también
        updatedActaWithId.customProjectEmail = newApproverEmail;
      } else if (!newApproverEmail && updatedActa.customProjectEmail) {
         // Si ya no hay aprobador definido para la nueva combinación, quizás deberíamos borrar el anterior
         // Pero por seguridad, mejor mantenerlo o advertir. 
         // En este caso, si el usuario cambió el proyecto a uno sin aprobador, 
         // el sistema intentará buscarlo de nuevo en assignActaToProjectResponsible
      }
      
      // Actualizar la lista de actas
      setActas(actas.map(acta => acta.id === editingActa.id ? updatedActaWithId : acta));
      
      // Actualizar el acta actual para la vista previa
      setCurrentActa(updatedActaWithId);
      
      // Limpiar el estado de edición
      console.log('🧹 Limpiando estado de edición');
      setEditingActa(null);
      
      // Mostrar mensaje de éxito
      setUpdateMessage({
        type: 'success',
        text: 'Acta actualizada correctamente'
      });
      
      console.log('✅ Acta actualizada exitosamente:', editingActa.id);
      
      // Ocultar el mensaje después de 3 segundos
      setTimeout(() => {
        setUpdateMessage(null);
      }, 3000);
      
      // Cambiar a la pestaña de vista previa
      setTimeout(() => {
        setTab(2); // Cambiar a la pestaña de vista previa
      }, 100);
      
      // Finalizar el estado de envío
      setIsSubmittingActa(false);
    } catch (error) {
      console.error('❌ Error al actualizar el acta:', error);
      console.error('🔍 editingActa en error:', editingActa);
      setUpdateMessage({
        type: 'error',
        text: 'Error al actualizar el acta. Por favor intente nuevamente.'
      });
      
      // Ocultar el mensaje después de 3 segundos
      setTimeout(() => {
        setUpdateMessage(null);
      }, 3000);
      
      // Finalizar el estado de envío
      setIsSubmittingActa(false);
    }
  };

  const handleTabChange = (newTab) => {
    setTab(newTab);
    setCurrentActa(null);
    setEditingActa(null);
    
    // Si estamos cambiando a la pestaña de historial, recargamos las actas
    if (newTab === 1) {
      // Establecer loading para mostrar indicador de carga si es necesario
      setLoading(true);
      // Recargar las actas desde Firebase
      fetchActas();
    }
  };

  // Función para manejar la edición de un acta
  const handleEditActa = (acta) => {
    // Verificar que solo el creador o un administrador pueda editar
    if (acta.creatorId !== user.uid && !hasRole(userRole, ROLES.GESTOR)) {
      alert('Solo el creador del acta o un administrador puede editarla.');
      return;
    }
    
    // Establecer el acta a editar
    setEditingActa(acta);
    
    // Cambiar a la pestaña del formulario
    setTab(0);
  };

  // Función para manejar la actualización de un acta
  const handleActaUpdate = (updatedActa) => {
    // Validar que updatedActa existe y tiene id
    if (!updatedActa || !updatedActa.id) {
      console.warn('handleActaUpdate: updatedActa es inválido', updatedActa);
      return;
    }
    
    // Actualizar el acta en el estado local
    setActas(prevActas => 
      prevActas.map(acta => 
        acta && acta.id === updatedActa.id ? updatedActa : acta
      )
    );
  };

  return (
    <Box sx={{...styles.container}}>
      <CssBaseline />
      
      {/* Barra superior */}
      <AppBar 
        position="fixed" 
        sx={{
          ...styles.appBar, 
          width: isDrawerOpen 
            ? `calc(100% - ${drawerWidth}px - ${isMobile ? '0px' : '3rem'})` 
            : `calc(100% - 4.5rem)`, 
          left: isDrawerOpen ? '17.5rem' : '4.5rem' 
        }}
        elevation={0}
      >
        <Toolbar sx={styles.toolbar}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <Typography 
              variant="h6" 
              color="primary" 
              sx={{
                ...styles.title,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                fontSize: { xs: '1rem', sm: '1.25rem' }
              }}
            >
              Gestión de actas
            </Typography>
            <Box onClick={handleClick} sx={{ display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer', '&:hover': { opacity: .8 } }}>
              <Typography variant="body2" color="#697a8d" sx={{ display: { xs: 'none', sm: 'block' } }}>
                {user.email.split('@')[0]}
              </Typography>
              <Box sx={{ background: '#697a8d', padding: '.5rem 1rem', borderRadius: 3 }}>
                <Typography color="#eee" fontWeight='bold'>
                  {user.email.split('@')[0].split('.')[0].split('')[0].toUpperCase()}
                </Typography>
              </Box>
            </Box>
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                sx={{
                  '& .MuiPaper-root': {
                    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                    borderRadius: '8px',
                    mt: 1
                  }
                }}
              >
                <Box sx={{ p: 1 }}>
                  <Button 
                    sx={{
                      color: '#fff',
                      border: 'none',
                      borderRadius: 2,
                      background: '#629bf885',
                      '&:hover': {
                        borderColor: 'tomato !important',
                        background: 'tomato',
                        transition: 'background 0.3s ease'
                      }
                    }} 
                    variant="outlined" 
                    onClick={() => {
                      handleClose();
                      handleLogout();
                    }} 
                    size="small"
                  >
                    Cerrar sesión
                  </Button>
                </Box>
            </Popover>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Menú lateral */}
      <Drawer
        variant="permanent"
        sx={{
          ...styles.drawer,
          width: isDrawerOpen ? drawerWidth : 72,
          '& .MuiDrawer-paper': {
            ...styles.drawer['& .MuiDrawer-paper'],
            width: isDrawerOpen ? drawerWidth : 72,
            overflowX: 'hidden',
            whiteSpace: 'nowrap'
          }
        }}
      >
        <Box sx={styles.drawerContainer}>
          <Box 
            component="img"
            src="https://www.inahta.org/wp-content/uploads/2024/04/IETS-Horizontal-PNG.png"
            alt="IETS Logo"
            sx={styles.logo}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, mb: 1 }}>
            {isDrawerOpen && (
              <Typography 
                variant="overline" 
                sx={styles.drawerTitle}
              >
                GESTIÓN DE ACTAS
              </Typography>
            )}
            <IconButton onClick={() => setIsDrawerOpen(!isDrawerOpen)} sx={{ 
              background: '#696cff', 
              color: '#fff', 
              padding: .5, 
              margin: 'auto',
              transition: 'all 0.3s ease',
              '&:hover': {
                background: '#5a5dff',
                transform: 'scale(1.05)',
                boxShadow: '0 2px 8px rgba(105, 108, 255, 0.4)'
              }
            }}>
              {isDrawerOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
            </IconButton>
          </Box>
          <List sx={styles.drawerList}>
            <ListItem 
              button 
              selected={tab === 0}
              onClick={() => handleTabChange(0)}
              sx={{ 
                ...styles.drawerItem,
                width: isDrawerOpen ? '88%' : '57%'
              }}
              
            >
              <ListItemIcon sx={{
                ...styles.drawerIcon, margin: isDrawerOpen ? '0!important' : 'auto auto auto -.5rem !important'
              }}>
                <NoteAddIcon />
              </ListItemIcon>
              {isDrawerOpen && (
                <ListItemText 
                  primary="Nueva acta" 
                  primaryTypographyProps={{
                    fontWeight: tab === 0 ? 600 : 400,
                    ...styles.drawerText,
                  }}
                />
              )}
            </ListItem>
            <ListItem 
              button 
              selected={tab === 1}
              onClick={() => handleTabChange(1)}
              sx={{ 
                ...styles.drawerItem,
                width: isDrawerOpen ? '88%' : '57%'
              }}
            >
              <ListItemIcon sx={{
                ...styles.drawerIcon,
                margin: isDrawerOpen? '0!important' : 'auto auto auto -.5rem!important'
              }}>
                <HistoryIcon />
              </ListItemIcon>
              {isDrawerOpen && (
                <ListItemText 
                  primary="Historial de actas"
                  primaryTypographyProps={{
                    fontWeight: tab === 1 ? 600 : 400,
                    ...styles.drawerText
                  }}
                />
              )}
            </ListItem>
            
            {/* Admin section - only visible to admin users */}
            {hasRole(userRole, ROLES.ADMIN) && (
              <>
                <Divider sx={{ my: 2, mx: 2 }} />
                <Box sx={{ px: 2, mb: 1 }}>
                  {isDrawerOpen && (
                    <Typography 
                      variant="overline" 
                      sx={styles.drawerTitle}
                    >
                      ADMINISTRACIÓN
                    </Typography>
                  )}
                </Box>
                <ListItem 
                  button 
                  selected={tab === 3}
                  onClick={() => setTab(3)}
                  sx={{...styles.drawerItem,  width: isDrawerOpen ? '88%' : '57%'}}
                >
                  <ListItemIcon sx={{
                    ...styles.drawerIcon,
                    color: tab === 3 ? 'primary.main' : 'text.secondary',
                    margin: isDrawerOpen? '0!important' : 'auto auto auto -.5rem!important'
                  }}>
                    <PeopleIcon />
                  </ListItemIcon>
                  {isDrawerOpen && (
                    <ListItemText 
                      primary="Usuarios" 
                      primaryTypographyProps={{
                        fontWeight: tab === 3 ? 600 : 400,
                        ...styles.drawerText
                      }}
                    />
                  )}
                </ListItem>
                <ListItem 
                  button 
                  selected={tab === 4}
                  onClick={() => setTab(4)}
                  sx={{...styles.drawerItem, width: isDrawerOpen ? '88%' : '57%'}}
                >
                  <ListItemIcon sx={{
                    ...styles.drawerIcon,
                    color: tab === 4 ? 'primary.main' : 'text.secondary',
                    margin: isDrawerOpen? '0!important' : 'auto auto auto -.5rem!important'
                  }}>
                    <DeleteIcon />
                  </ListItemIcon>
                  {isDrawerOpen && (
                    <ListItemText 
                      primary="Eliminar actas" 
                      primaryTypographyProps={{
                        fontWeight: tab === 4 ? 600 : 400,
                        ...styles.drawerText
                      }}
                    />
                  )}
                </ListItem>
                <ListItem 
                  button 
                  selected={tab === 5}
                  onClick={() => setTab(5)}
                  sx={{...styles.drawerItem, width: isDrawerOpen ? '88%' : '57%'}}
                >
                  <ListItemIcon sx={{
                    ...styles.drawerIcon,
                    color: tab === 5 ? 'primary.main' : 'text.secondary',
                    margin: isDrawerOpen? '0!important' : 'auto auto auto -.5rem!important'
                  }}>
                    <AssessmentIcon />
                  </ListItemIcon>
                  {isDrawerOpen && (
                    <ListItemText 
                      primary="Logs" 
                      primaryTypographyProps={{
                        fontWeight: tab === 5 ? 600 : 400,
                        ...styles.drawerText
                      }}
                    />
                  )}
                </ListItem>
                {/* <ListItem 
                  button 
                  selected={tab === 6}
                  onClick={() => setTab(6)}
                  sx={styles.drawerItem}
                >
                  <ListItemIcon sx={{
                    ...styles.drawerIcon,
                    color: tab === 6 ? 'primary.main' : 'text.secondary'
                  }}>
                    <PeopleIcon />
                  </ListItemIcon>
                  {isDrawerOpen && (
                    <ListItemText 
                      primary="Asignar Proyectos" 
                      primaryTypographyProps={{
                        fontWeight: tab === 6 ? 600 : 400,
                        ...styles.drawerText
                      }}
                    />
                  )}
                </ListItem> */}
                <ListItem 
                  button 
                  selected={tab === 7}
                  onClick={() => setTab(7)}
                  sx={{...styles.drawerItem, width: isDrawerOpen ? '88%' : '57%'}}
                >
                  <ListItemIcon sx={{
                    ...styles.drawerIcon,
                    color: tab === 7 ? 'primary.main' : 'text.secondary',
                    margin: isDrawerOpen? '0!important' : 'auto auto auto -.5rem!important'
                  }}>
                    <GroupsIcon />
                  </ListItemIcon>
                  {isDrawerOpen && (
                    <ListItemText 
                      primary="Comités" 
                      primaryTypographyProps={{
                        fontWeight: tab === 7 ? 600 : 400,
                        ...styles.drawerText
                      }}
                    />
                  )}
                </ListItem>
                <ListItem 
                  button 
                  selected={tab === 9}
                  onClick={() => setTab(9)}
                  sx={{...styles.drawerItem, width: isDrawerOpen ? '88%' : '57%'}}
                >
                  <ListItemIcon sx={{
                    ...styles.drawerIcon,
                    color: tab === 9 ? 'primary.main' : 'text.secondary',
                    margin: isDrawerOpen? '0!important' : 'auto auto auto -.5rem!important'
                  }}>
                    <CategoryIcon />
                  </ListItemIcon>
                  {isDrawerOpen && (
                    <ListItemText 
                      primary="Categorías Otro" 
                      primaryTypographyProps={{
                        fontWeight: tab === 9 ? 600 : 400,
                        ...styles.drawerText
                      }}
                    />
                  )}
                </ListItem>
                <ListItem 
                  button 
                  selected={tab === 10}
                  onClick={() => setTab(10)}
                  sx={{...styles.drawerItem, width: isDrawerOpen ? '88%' : '57%'}}
                >
                  <ListItemIcon sx={{
                    ...styles.drawerIcon,
                    color: tab === 10 ? 'primary.main' : 'text.secondary',
                    margin: isDrawerOpen? '0!important' : 'auto auto auto -.5rem!important'
                  }}>
                    <AssignmentIndIcon />
                  </ListItemIcon>
                  {isDrawerOpen && (
                    <ListItemText 
                      primary="Aprobadores" 
                      primaryTypographyProps={{
                        fontWeight: tab === 10 ? 600 : 400,
                        ...styles.drawerText
                      }}
                    />
                  )}
                </ListItem>
                <ListItem 
                  button 
                  selected={tab === 8}
                  onClick={() => setTab(8)}
                  sx={{...styles.drawerItem, width: isDrawerOpen ? '88%' : '57%'}}
                >
                  <ListItemIcon sx={{
                    ...styles.drawerIcon,
                    color: tab === 8 ? 'primary.main' : 'text.secondary',
                    margin: isDrawerOpen? '0!important' : 'auto auto auto -.5rem!important'
                  }}>
                    <CloudIcon />
                  </ListItemIcon>
                  {isDrawerOpen && (
                    <ListItemText 
                      primary="OneDrive" 
                      primaryTypographyProps={{
                        fontWeight: tab === 8 ? 600 : 400,
                        ...styles.drawerText
                      }}
                    />
                  )}
                </ListItem>
                <ListItem 
                  button 
                  selected={tab === 11}
                  onClick={() => setTab(11)}
                  sx={{...styles.drawerItem, width: isDrawerOpen ? '88%' : '57%'}}
                >
                  <ListItemIcon sx={{
                    ...styles.drawerIcon,
                    color: tab === 11 ? 'primary.main' : 'text.secondary',
                    margin: isDrawerOpen? '0!important' : 'auto auto auto -.5rem!important'
                  }}>
                    <SmartToyIcon />
                  </ListItemIcon>
                  {isDrawerOpen && (
                    <ListItemText 
                      primary="Configuración IA" 
                      primaryTypographyProps={{
                        fontWeight: tab === 11 ? 600 : 400,
                        ...styles.drawerText
                      }}
                    />
                  )}
                </ListItem>
              </>
            )}
          </List>
        </Box>
      </Drawer>

      {/* Contenido principal */}
      <Box 
        component="main" 
        sx={{...styles.mainContent, overflowX: 'hidden'}}
      >
        <Toolbar sx={{ height: { xs: 64, md: 70 }, mb: 2 }} /> {/* Espaciador para la barra superior */}
        
        {updateMessage && (
          <Alert 
            severity={updateMessage.type} 
            sx={{ mb: 2, mx: 3 }}
            onClose={() => setUpdateMessage(null)}
          >
            {updateMessage.text}
          </Alert>
        )}
        
        <Fade in={tab === 0} timeout={500} unmountOnExit>
          <Box>{tab === 0 && <ActaForm 
            onSubmit={handleSubmit} 
            nextActaNumber={nextActaNumber} 
            editingActa={editingActa} 
            userRole={userRole}
            onNavigateToAiConfig={() => setTab(11)}
          />}</Box>
        </Fade>
        
        <Fade in={tab === 1} timeout={500} unmountOnExit>
          <Box>{tab === 1 && <HistorialActas 
            actas={actas} 
            userId={user.uid} 
            userEmail={user.email}
            onEditActa={handleEditActa} 
            userRole={userRole}
            onActaUpdate={handleActaUpdate}
            onActaDeleted={(deletedActaId, actaDetails) => {
              // Actualizar el estado global de actas cuando se elimina una
              setActas(prevActas => prevActas.filter(acta => acta.id !== deletedActaId));
              
              // Registrar la eliminación en el sistema de logs
              logUserAction(LOG_ACTIONS.DELETE, 'acta', deletedActaId, actaDetails);
            }}
          />}</Box>
        </Fade>
        
        <Zoom in={tab === 2 && currentActa !== null} timeout={500} unmountOnExit>
          <Box>
            {tab === 2 && currentActa && (
              <Paper sx={styles.pdfContainer}>
                <PDFViewer width="100%" height="100%" style={{ border: 'none' }}>
                  <ActaPDF data={currentActa} />
                </PDFViewer>
              </Paper>
            )}
          </Box>
        </Zoom>
        
        {/* User Administration - only for admin users */}
        <Fade in={tab === 3} timeout={500} unmountOnExit>
          <Box>{tab === 3 && hasRole(userRole, ROLES.ADMIN) && <UserAdmin />}</Box>
        </Fade>
        
        {/* Project Assignment - only for admin users */}
        <Fade in={tab === 6} timeout={500} unmountOnExit>
          <Box>{tab === 6 && hasRole(userRole, ROLES.ADMIN) && <UserAdmin />}</Box>
        </Fade>
        
        {/* Acta Administration - only for admin users */}
        <Fade in={tab === 4} timeout={500} unmountOnExit>
          <Box>{tab === 4 && hasRole(userRole, ROLES.ADMIN) && <AdminActas 
            onActaDeleted={(deletedActaId, actaDetails) => {
              // Actualizar el estado global de actas cuando se elimina una
              setActas(prevActas => prevActas.filter(acta => acta.id !== deletedActaId));
              
              // Registrar la eliminación en el sistema de logs
              logUserAction(LOG_ACTIONS.DELETE, 'acta', deletedActaId, actaDetails);
            }}
            onEditActa={handleEditActa}
          />}</Box>
        </Fade>
        
        {/* Logs Administration - only for admin users */}
        <Fade in={tab === 5} timeout={500} unmountOnExit>
          <Box>{tab === 5 && hasRole(userRole, ROLES.ADMIN) && <AdminLogs />}</Box>
        </Fade>
        
        {/* Comites Administration - only for admin users */}
        <Fade in={tab === 7} timeout={500} unmountOnExit>
          <Box>{tab === 7 && hasRole(userRole, ROLES.ADMIN) && <AdminComites />}</Box>
        </Fade>

        {/* Categorias Otro Administration - only for admin users */}
        <Fade in={tab === 9} timeout={500} unmountOnExit>
          <Box>{tab === 9 && hasRole(userRole, ROLES.ADMIN) && <AdminCategoriasOtro />}</Box>
        </Fade>
        
        {/* Approvers Administration - only for admin users */}
        <Fade in={tab === 10} timeout={500} unmountOnExit>
          <Box>{tab === 10 && hasRole(userRole, ROLES.ADMIN) && <AdminApprovers />}</Box>
        </Fade>
        
        {/* OneDrive Management - only for admin users */}
        <Fade in={tab === 8} timeout={500} unmountOnExit>
          <Box>{tab === 8 && hasRole(userRole, ROLES.ADMIN) && <OneDriveManager 
            actas={actas} 
            onActaUpdate={(updatedActa) => {
              if (!updatedActa || !updatedActa.id) {
                console.warn('OneDriveManager onActaUpdate: updatedActa es inválido', updatedActa);
                return;
              }
              setActas(actas.map(acta => acta && acta.id === updatedActa.id ? updatedActa : acta));
            }}
          />}</Box>
        </Fade>

        {/* AI Configuration - only for admin users */}
        <Fade in={tab === 11} timeout={500} unmountOnExit>
          <Box>{tab === 11 && hasRole(userRole, ROLES.ADMIN) && <AdminAiConfig />}</Box>
        </Fade>
      </Box>

      {/* Dialog para solicitar correo del responsable del proyecto */}
      <Dialog 
        open={emailPopupOpen} 
        onClose={handleEmailPopupCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          📋 Responsable del Acta
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Para finalizar el envío del acta, debe asignar un responsable que será el 
            encargado de la aprobación.
          </Typography>
          
          {/* Información del Acta */}
          <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
            <Typography variant="h6" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
              📄 Información del Acta
            </Typography>
            {pendingActaData && (
              <>
                {pendingActaData.isDirecto ? (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Tipo de Acta:</strong> {pendingActaData.tipoProyecto || pendingActaData.tipoActa}
                  </Typography>
                ) : (
                  <>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Contrato:</strong> {pendingActaData.tipoProyectos && pendingActaData.tipoProyectos.length > 0 
                        ? pendingActaData.tipoProyectos.join(', ') 
                        : 'No especificado'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Proyecto:</strong> {pendingActaData.productosProyecto && pendingActaData.productosProyecto.length > 0 
                        ? pendingActaData.productosProyecto.join(', ') 
                        : 'TRANSVERSAL'}
                    </Typography>
                  </>
                )}
              </>
            )}
          </Paper>
          
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            ⚠️ Correo del Responsable del Acta *
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Correo electrónico del responsable"
            type="email"
            fullWidth
            variant="outlined"
            value={customEmail}
            onChange={(e) => setCustomEmail(e.target.value)}
            placeholder="ejemplo@empresa.com"
            helperText="Este será el encargado de aprobar el acta y aparecerá en el historial"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEmailPopupCancel} color="secondary">
            Cancelar
          </Button>
          <Button 
            onClick={handleCustomEmailSubmit} 
            color="primary" 
            variant="contained"
            disabled={!customEmail.trim() || isSubmittingActa}
            startIcon={isSubmittingActa ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isSubmittingActa ? 'Enviando...' : '✅ Enviar para Aprobación'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de nueva versión */}
      <Dialog
        open={showVersionDialog}
        disableEscapeKeyDown
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: 400
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
          🔄 Nueva Versión Disponible
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2, textAlign: 'center' }}>
            Se ha detectado una nueva versión de la aplicación.
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary', textAlign: 'center' }}>
            Para garantizar el correcto funcionamiento y evitar conflictos,
            es necesario recargar la página.
          </Typography>
          <Typography variant="body2" sx={{ color: 'warning.main', textAlign: 'center', fontWeight: 'medium' }}>
            ⚠️ Esta acción recargará automáticamente la aplicación
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <Button 
            onClick={handleForceReload}
            color="primary" 
            variant="contained"
            disabled={isReloading}
            startIcon={isReloading ? <CircularProgress size={20} color="inherit" /> : null}
            sx={{ minWidth: 150 }}
          >
            {isReloading ? 'Recargando...' : '🔄 Recargar Ahora'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default App;
